import { AccountTree, Add, Delete, Merge, TableChart, TableRows } from '@mui/icons-material';
import React, { lazy, useState } from 'react';
import { useSelector } from 'react-redux';
import './SkillCategoriesPage.css';
import { EEntityType, ELinks } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { SkillCategoryCard } from '@src/App/Components/Cards/EntityCards/SkillCategories/SkillCategoryCard/SkillCategoryCard';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import CategorizeDialogMain from '@src/App/Components/Dialogs/CategorizeDialog/CategorizeDialogMain';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import SkillCategoryDialog from '@src/App/Components/Dialogs/SkillCategoryDialog/SkillCategoryDialog';
import SkillCategoryOverlay from '@src/App/Components/Overlays/SkillCategoryOverlay/SkillCategoryOverlay';
import EnhancedTable, { SortOrder } from '@src/App/Components/Tables/DataTable/DataTable';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type { SkillCategory } from '@src/Objects/SkillCategory';
import type { AppState } from '@store/store';
import { useNavigate } from 'react-router-dom';

import EntitiesPage, { EViewType, type IEntitiesPageAction } from '../../Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '../../Generic/EntitiesPage/ViewTypeMenu';

// import { SkillCategoryTree } from './SkillCategoryTree';

const SkillCategoryTree = lazy(() => import('./SkillCategoryTree'));

/**
 * Represents the SkillCategoriesPage component.
 *
 * @returns {JSX.Element} The JSX element representing the SkillCategoriesPage.
 */
function SkillCategoriesPage() {
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skillCategories;

    const entities = useSelector((state: AppState) => state.company.skillCategories);
    const permissions = useSelector((state: AppState) => state.permissions);

    const isDense = (width: number) => width <= 1000;
    const [selected, setSelected] = useState<number[]>([]);
    const [activeRole, setActiveRole] = useState('admin');

    const roles: IMvpRole[] = [
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'skills-catalog',
            label: 'Fähigkeiten Katalog',
            icon: getAppIcon(EIconCategory.CATALOG),
            onClick: () => navigate(`/${ELinks.SKILLS}`),
            variant: 'primary',
        },
    ];

    const types: IViewType<SkillCategory>[] = [
        {
            id: EViewType.TREE,
            title: langStrings.oftenUsed.treeStructure,
            icon: <AccountTree fontSize="small" />,
            renderContainer: (allEntities, innerEntries) => <SkillCategoryTree elements={innerEntries} />,
        },
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (allEntities, innerEntries, width, selected) => (
                <EnhancedTable
                    id={EEntityType.SKILL_CATEGORY}
                    dense={isDense(width)}
                    noCheckbox={
                        !(
                            (checkPermissions('SkillCategories', 'Delete', permissions) ||
                                checkPermissions('SkillCategories', 'Add', permissions)) &&
                            checkPermissions('SkillCategories', 'Edit', permissions)
                        )
                    }
                    selected={selected}
                    setSelected={setSelected}
                    order={SortOrder.ASC}
                    orderBy="title"
                    fields={[
                        {
                            id: 'id',
                            label: 'ID',
                            disablePadding: isDense(width),
                            align: 'left',
                            type: 'string',
                        },
                        {
                            id: 'title',
                            label: langStrings.skillcategoryName,
                            disablePadding: isDense(width),
                            align: 'left',
                            type: 'JSX.Element',
                            link: `/${ELinks.SKILLCATEGORIES}/`,
                        },
                        {
                            id: 'description',
                            label: langStrings.description,
                            disablePadding: isDense(width),
                            align: 'left',
                            minWidth: 1000,
                            type: 'description',
                        },
                    ]}
                    rows={innerEntries.map((entity) => ({
                        id: entity.id,
                        title: {
                            value: (
                                <SkillCategoryOverlay skillCategoryId={entity.id}>{entity.title} </SkillCategoryOverlay>
                            ),
                            orderKey: entity.title,
                        },
                        description: entity.description,
                    }))}
                />
            ),
        },
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities, innerEntries) => (
                <Container>
                    {innerEntries.map((entity) => (
                        <SkillCategoryCard key={entity.id} cat={entity} />
                    ))}
                </Container>
            ),
        },
    ];

    const actions: IEntitiesPageAction[] = [
        ...(checkPermissions('SkillCategories', 'Add', permissions)
            ? [
                  {
                      id: 'create',
                      text: langStrings.create,
                      icon: <Add />,
                      filter() {
                          if (!checkPermissions('SkillCategories', 'Add', permissions)) return false;
                          return true;
                          // return selectedCount === 1;
                      },
                      action() {
                          return openDialog(<SkillCategoryDialog id="SkillCategoryDialog" resolve={() => {}} />);
                      },
                  },
              ]
            : []),
        ...(checkPermissions('SkillCategories', 'Delete', permissions)
            ? [
                  {
                      id: 'delete',
                      text: langStrings.delete,
                      icon: <Delete />,
                      filter(selectedCount: number) {
                          return selectedCount === 1;
                      },
                      action() {
                          if (entities) {
                              const entity = entities.find((s) => selected.indexOf(s.id) > -1);
                              if (entity) {
                                  entity.delete();
                                  setSelected([]);
                              }
                          }
                      },
                  },
              ]
            : []),
        ...(checkPermissions('SkillCategories', 'Delete', permissions) &&
        checkPermissions('SkillCategories', 'Add', permissions) &&
        checkPermissions('SkillCategories', 'Edit', permissions)
            ? [
                  {
                      id: 'categorize',
                      text: langStrings.categorize,
                      icon: <Merge />,
                      filter() {
                          if (
                              !checkPermissions('SkillCategories', 'Delete', permissions) ||
                              !checkPermissions('SkillCategories', 'Add', permissions) ||
                              !checkPermissions('SkillCategories', 'Edit', permissions)
                          )
                              return false;
                          return true; //TODO: Activate again
                      },
                      action() {
                          openDialog(<CategorizeDialogMain id="CategorizeDialogMain" resolve={() => {}} />);
                      },
                  },
              ]
            : []),
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Kategorien"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            <EntitiesPage
                title={langStrings.skillCategories}
                entities={entities}
                selected={selected}
                loadingError={
                    checkPermissions('SkillCategories', 'Retrieve', permissions)
                        ? null
                        : { statusCode: 401, message: 'Forbidden' }
                }
                filter={(entity, selectedView, searchText) =>
                    selectedView.filter(entity) && textContainsLowerCase(searchText, entity.title)
                }
                setSelected={setSelected}
                views={[
                    {
                        id: 'all',
                        title: langStrings.oftenUsed.allEntries,
                        filter() {
                            return true;
                        },
                    },
                ]}
                types={types}
                actions={actions}
            />
        </>
    );
}
export default SkillCategoriesPage;
